import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { Pencil, ArrowRight, Building2, MapPin, Phone, Mail, User, Coins, Plus, X, Trash2, Loader2 } from 'lucide-react';
import { Button, Spinner, PageHeader, Badge, subtleNeo } from '@brainsway/ui';
import { localizedName } from '@/utils/localized';
import { useHospitalDetail, useHospitalBonuses, useCreateHospitalBonus, useUpdateHospitalBonus, useDeleteHospitalBonus } from '../hooks';
import { TherapistAssignment } from '../components/TherapistAssignment';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useEmploymentRoles, useEmploymentRoleRates } from '@/features/admin';
import { ADMIN_COLORS } from '../../admin/utils/adminAnimations';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import type { HospitalBonus } from '../types';

export function HospitalDetailPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const sym = useCurrencySymbol();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: hospital, isLoading } = useHospitalDetail(id);
  const { hasPermission } = usePermissions();

  // Hospital bonuses
  const { data: bonuses, isLoading: isLoadingBonuses } = useHospitalBonuses(id);
  const { data: employmentRolesData } = useEmploymentRoles({ isActive: 'true', limit: 100 });
  const employmentRoles = employmentRolesData?.employmentRoles ?? [];

  const createMutation = useCreateHospitalBonus();
  const updateMutation = useUpdateHospitalBonus();
  const deleteMutation = useDeleteHospitalBonus();

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addRoleId, setAddRoleId] = useState('');
  const [addRateId, setAddRateId] = useState('');
  const [addAmount, setAddAmount] = useState('');

  // Edit form state
  const [editingBonus, setEditingBonus] = useState<HospitalBonus | null>(null);
  const [editAmount, setEditAmount] = useState('');

  // Fetch rates for the selected role in the add form
  const { data: addRoleRates } = useEmploymentRoleRates(addRoleId || undefined);

  const resetAddForm = () => {
    setAddRoleId('');
    setAddRateId('');
    setAddAmount('');
  };

  const handleAdd = async () => {
    if (!id || !addRateId || !addAmount) return;
    await createMutation.mutateAsync({
      hospitalId: id,
      data: {
        employmentRoleRateId: addRateId,
        bonusAmount: parseFloat(addAmount),
      },
    });
    resetAddForm();
    setShowAddForm(false);
  };

  const startEdit = (b: HospitalBonus) => {
    setEditingBonus(b);
    setEditAmount(String(b.bonusAmount));
    setShowAddForm(false);
  };

  const cancelEdit = () => {
    setEditingBonus(null);
    setEditAmount('');
  };

  const handleUpdate = async () => {
    if (!id || !editingBonus || !editAmount) return;
    await updateMutation.mutateAsync({
      hospitalId: id,
      bonusId: editingBonus.id,
      data: { bonusAmount: parseFloat(editAmount) },
    });
    cancelEdit();
  };

  const handleDelete = async (bonusId: string) => {
    if (!id) return;
    await deleteMutation.mutateAsync({ hospitalId: id, bonusId });
  };

  const cardStyle = {
    backgroundColor: ADMIN_COLORS.surface,
    border: `1px solid ${ADMIN_COLORS.border}`,
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!hospital) {
    return (
      <div className="py-20 text-center text-neutral-500">
        {t('common.notFound', 'Not found')}
      </div>
    );
  }

  return (
    <motion.div initial="hidden" animate="visible" variants={subtleNeo.fadeIn} className="mx-auto max-w-2xl space-y-5">
      <PageHeader
        title={localizedName(hospital, i18n.language)}
        backButton={
          <Link to="/admin/hospitals">
            <Button variant="ghost" size="icon"><ArrowRight className="h-5 w-5 rtl:rotate-180" /></Button>
          </Link>
        }
        actions={
          hasPermission('action:manage_hospitals') ? (
            <Button variant="outline" onClick={() => navigate(`/admin/hospitals/${id}/edit`)}>
              <Pencil className="h-4 w-4 me-1" />
              {t('common.edit')}
            </Button>
          ) : undefined
        }
      />

      <div
        className="rounded-3xl overflow-hidden glass-surface"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <div className="p-6 space-y-4">
          {/* Status */}
          <div className="flex justify-between">
            <span className="text-sm text-neutral-500">{t('status.label', 'Status')}</span>
            <Badge variant={hospital.isActive ? 'success' : 'default'}>
              {hospital.isActive ? t('hospitals.active', 'Active') : t('hospitals.inactive', 'Inactive')}
            </Badge>
          </div>

          {/* Code */}
          <div className="flex justify-between">
            <span className="text-sm text-neutral-500">{t('hospitals.code', 'Code')}</span>
            <span className="text-sm font-mono font-medium text-neutral-800">{hospital.code}</span>
          </div>

          {/* City */}
          <div className="flex justify-between">
            <span className="flex items-center gap-2 text-sm text-neutral-500">
              <MapPin className="h-4 w-4" /> {t('hospitals.city', 'City')}
            </span>
            <span className="text-sm font-medium text-neutral-800">{hospital.city}</span>
          </div>

          {/* Address */}
          {hospital.address && (
            <div className="flex justify-between">
              <span className="text-sm text-neutral-500">{t('hospitals.address', 'Address')}</span>
              <span className="text-sm font-medium text-neutral-800 text-end max-w-[60%]">{hospital.address}</span>
            </div>
          )}

          {/* Department */}
          {hospital.department && (
            <div className="flex justify-between">
              <span className="flex items-center gap-2 text-sm text-neutral-500">
                <Building2 className="h-4 w-4" /> {t('hospitals.department', 'Department')}
              </span>
              <span className="text-sm font-medium text-neutral-800">{hospital.department}</span>
            </div>
          )}

          {/* Contact */}
          {(hospital.contactName || hospital.contactPhone || hospital.contactEmail) && (
            <div className="border-t-[2px] border-white/30 pt-3 space-y-2">
              <p className="text-xs font-bold text-slate-500 uppercase">{t('hospitals.contact', 'Contact')}</p>
              {hospital.contactName && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-3.5 w-3.5 text-slate-500" />
                  <span>{hospital.contactName}</span>
                </div>
              )}
              {hospital.contactPhone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-3.5 w-3.5 text-slate-500" />
                  <span dir="ltr">{hospital.contactPhone}</span>
                </div>
              )}
              {hospital.contactEmail && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-3.5 w-3.5 text-slate-500" />
                  <span dir="ltr">{hospital.contactEmail}</span>
                </div>
              )}
            </div>
          )}

          {/* Equipment Notes */}
          {hospital.equipmentNotes && (
            <div className="border-t-[2px] border-white/30 pt-3">
              <p className="text-xs font-bold text-slate-500 uppercase mb-1">{t('hospitals.equipmentNotes', 'Equipment')}</p>
              <p className="text-sm font-medium text-neutral-800 whitespace-pre-wrap">{hospital.equipmentNotes}</p>
            </div>
          )}

          {/* Notes */}
          {hospital.notes && (
            <div className="border-t-[2px] border-white/30 pt-3">
              <p className="text-xs font-bold text-slate-500 uppercase mb-1">{t('reports.notes')}</p>
              <p className="text-sm font-medium text-neutral-800 whitespace-pre-wrap">{hospital.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Assigned Therapists */}
      <div
        className="rounded-3xl overflow-hidden glass-surface"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
        }}
      >
        <div
          className="px-6 py-2.5"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            borderBottom: `1px solid ${ADMIN_COLORS.border}`,
          }}
        >
          <h3 className="font-semibold text-xs sm:text-sm">
            {t('hospitals.assignedEmployees', 'Assigned Employees')}
          </h3>
        </div>
        <div className="px-6 py-4">
          <TherapistAssignment
            therapists={hospital.therapists || []}
            isAdmin={hasPermission('action:manage_hospitals')}
          />
        </div>
      </div>

      {/* Hospital Bonuses */}
      {hasPermission('action:manage_rates') && (
        <div
          className="rounded-3xl overflow-hidden glass-surface"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            border: `1px solid ${ADMIN_COLORS.border}`,
            boxShadow: ADMIN_COLORS.shadow,
          }}
        >
          <div
            className="px-6 py-2.5 flex items-center justify-between"
            style={{
              backgroundColor: ADMIN_COLORS.surface,
              borderBottom: `1px solid ${ADMIN_COLORS.border}`,
            }}
          >
            <div className="flex items-center gap-2">
              <Coins className="w-4 h-4 opacity-60" />
              <h3 className="font-semibold text-xs sm:text-sm">
                {t('hospitals.bonuses.title')}
              </h3>
            </div>
            <button
              onClick={() => {
                setShowAddForm(!showAddForm);
                cancelEdit();
              }}
              className="flex items-center gap-1.5 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors cursor-pointer"
              style={cardStyle}
            >
              {showAddForm ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
              {showAddForm ? t('common.cancel') : t('hospitals.bonuses.add')}
            </button>
          </div>

          <div className="px-6 py-4 space-y-3">
            {/* Add Form */}
            {showAddForm && (
              <div className="space-y-2 p-3 rounded-xl" style={{ backgroundColor: ADMIN_COLORS.surface, border: `1px solid ${ADMIN_COLORS.border}` }}>
                <select
                  value={addRoleId}
                  onChange={(e) => {
                    setAddRoleId(e.target.value);
                    setAddRateId('');
                  }}
                  className="w-full p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
                  style={{ border: `1px solid ${ADMIN_COLORS.border}`, backgroundColor: '#fff' }}
                >
                  <option value="">{t('hospitals.bonuses.selectRole')}</option>
                  {employmentRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {localizedName(role, lang)}
                    </option>
                  ))}
                </select>
                <select
                  value={addRateId}
                  onChange={(e) => setAddRateId(e.target.value)}
                  disabled={!addRoleId}
                  className="w-full p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer disabled:opacity-50"
                  style={{ border: `1px solid ${ADMIN_COLORS.border}`, backgroundColor: '#fff' }}
                >
                  <option value="">{t('hospitals.bonuses.selectReportType')}</option>
                  {(addRoleRates ?? []).map((rate) => (
                    <option key={rate.id} value={rate.id}>
                      {localizedName(rate.reportType, lang)}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  value={addAmount}
                  onChange={(e) => setAddAmount(e.target.value)}
                  min={0}
                  step={0.01}
                  placeholder={t('hospitals.bonuses.amount')}
                  className="w-full p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ border: `1px solid ${ADMIN_COLORS.border}`, backgroundColor: '#fff' }}
                />
                <button
                  onClick={handleAdd}
                  disabled={!addRateId || !addAmount || createMutation.isPending}
                  className="w-full px-2.5 py-1.5 rounded-lg text-xs font-bold cursor-pointer disabled:opacity-50 text-white"
                  style={{ backgroundColor: 'var(--color-primary-500)' }}
                >
                  {createMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : t('common.add')}
                </button>
              </div>
            )}

            {/* Edit Form */}
            {editingBonus && (
              <div className="space-y-2 p-3 rounded-xl" style={{ backgroundColor: 'rgba(254, 249, 195, 0.5)', border: `1px solid ${ADMIN_COLORS.border}` }}>
                <div
                  className="p-1.5 rounded-lg text-sm font-semibold"
                  style={{ border: `1px solid ${ADMIN_COLORS.border}`, backgroundColor: ADMIN_COLORS.surface }}
                >
                  {localizedName(editingBonus.employmentRoleRate.employmentRole, lang)}
                  {' > '}
                  {localizedName(editingBonus.employmentRoleRate.reportType, lang)}
                </div>
                <input
                  type="number"
                  value={editAmount}
                  onChange={(e) => setEditAmount(e.target.value)}
                  min={0}
                  step={0.01}
                  placeholder={t('hospitals.bonuses.amount')}
                  className="w-full p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                  style={{ border: `1px solid ${ADMIN_COLORS.border}`, backgroundColor: '#fff' }}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleUpdate}
                    disabled={!editAmount || updateMutation.isPending}
                    className="flex-1 px-2.5 py-1.5 rounded-lg text-xs font-bold cursor-pointer disabled:opacity-50 text-white"
                    style={{ backgroundColor: 'var(--color-primary-500)' }}
                  >
                    {updateMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : t('common.save')}
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="px-2 py-1.5 rounded-lg text-xs font-bold cursor-pointer glass-surface"
                    style={cardStyle}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Bonuses List */}
            {isLoadingBonuses ? (
              <div className="flex justify-center py-3">
                <Loader2 className="w-4 h-4 animate-spin" />
              </div>
            ) : (bonuses || []).length === 0 ? (
              <p className="text-xs opacity-50 text-center py-2">{t('hospitals.bonuses.noResults')}</p>
            ) : (
              <div className="space-y-1">
                {(bonuses || []).map((b) => (
                  <div
                    key={b.id}
                    className="flex items-center justify-between py-1.5 px-2 rounded-lg"
                    style={{ backgroundColor: ADMIN_COLORS.surface }}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Coins className="w-3 h-3 opacity-40 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-xs font-semibold truncate block">
                          {localizedName(b.employmentRoleRate.employmentRole, lang)}
                          {' > '}
                          {localizedName(b.employmentRoleRate.reportType, lang)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge variant="default" className="text-[10px]">
                        +{b.bonusAmount} {sym} / {t('hospitals.bonuses.perReport')}
                      </Badge>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(b);
                        }}
                        className="p-1 rounded hover:bg-blue-100 cursor-pointer"
                      >
                        <Pencil className="w-3 h-3 text-blue-500" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(b.id);
                        }}
                        disabled={deleteMutation.isPending}
                        className="p-1 rounded hover:bg-red-100 cursor-pointer disabled:opacity-50"
                      >
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}
